import api from './api';
import thermalAgentService from './thermalAgentService';

export const barcodePrintService = {
  // Templates API
  async getTemplates(params = {}) {
    const res = await api.get('/barcode-master/templates', { params });
    return res.data;
  },

  async getTemplateById(id) {
    const res = await api.get(`/barcode-master/templates/${id}`);
    return res.data;
  },

  async createTemplate(data) {
    const res = await api.post('/barcode-master/templates', data);
    return res.data;
  },

  async updateTemplate(id, data) {
    const res = await api.put(`/barcode-master/templates/${id}`, data);
    return res.data;
  },

  async toggleTemplateActive(id, isActive) {
    const res = await api.post(`/barcode-master/templates/${id}/toggle-active`, { isActive });
    return res.data;
  },

  async duplicateTemplate(id) {
    const res = await api.post(`/barcode-master/templates/${id}/duplicate`);
    return res.data;
  },

  async getTemplateVersions(id) {
    const res = await api.get(`/barcode-master/templates/${id}/versions`);
    return res.data;
  },

  // Printers API
  async getPrinters(params = {}) {
    const res = await api.get('/barcode-master/printers', { params });
    return res.data;
  },

  async getPrinterById(id) {
    const res = await api.get(`/barcode-master/printers/${id}`);
    return res.data;
  },

  async savePrinter(data) {
    const res = await api.post('/barcode-master/printers', data);
    return res.data;
  },

  async testPrinterConnection(id) {
    const res = await api.post(`/barcode-master/printers/${id}/test-connection`);
    return res.data;
  },

  // Mappings API
  async getMappings(params = {}) {
    const res = await api.get('/barcode-master/mappings', { params });
    return res.data;
  },

  async saveMapping(data) {
    const res = await api.post('/barcode-master/mappings', data);
    return res.data;
  },

  async deleteMapping(id) {
    const res = await api.delete(`/barcode-master/mappings/${id}`);
    return res.data;
  },

  // Deterministic Resolution Engine (Technician calls this without configuring anything)
  async resolvePrintConfig(sampleCriteria) {
    const res = await api.post('/barcode-master/resolve', {
      centreId: sampleCriteria.centreId || null,
      departmentId: sampleCriteria.departmentId || null,
      departmentName: sampleCriteria.departmentName || null,
      sampleTypeId: sampleCriteria.sampleTypeId || null,
      sampleTypeName: sampleCriteria.sampleTypeName || null,
      containerTypeId: sampleCriteria.containerTypeId || null,
      containerTypeName: sampleCriteria.containerName || sampleCriteria.containerTypeName || null,
      testId: sampleCriteria.testId || null,
      barcodeNo: sampleCriteria.barcodeNo || null
    });
    return res.data;
  },

  // Duplicate Print Check
  async checkDuplicatePrint({ testId, barcodeNo, ledgerTransactionNo }) {
    const res = await api.post('/barcode-master/check-duplicate', {
      testId: testId || null,
      barcodeNo: barcodeNo || null,
      ledgerTransactionNo: ledgerTransactionNo || null
    });
    return res.data;
  },

  // Log Print Job Audit
  async logPrintJob(jobData) {
    const res = await api.post('/barcode-master/jobs', jobData);
    return res.data;
  },

  // Query Print Job Audit History
  async getPrintJobs(params = {}) {
    const res = await api.get('/barcode-master/jobs', { params });
    return res.data;
  },

  // Generate Direct Thermal Payload (ZPL / TSPL / EPL)
  async generateThermalPayload(data) {
    const res = await api.post('/barcode-master/generate-payload', data);
    return res.data;
  },

  /**
   * Complete End-to-End Autonomous Printing for Phlebotomist & Technician:
   * 1. Resolves active template and printer automatically based on sample metadata.
   * 2. Checks duplicate print status.
   * 3. Dispatches payload to Local Print Agent or falls back to vector thermal printing.
   * 4. Logs audit record.
   */
  async executePrintWorkflow({ sample, isReprint = false, reprintReason = null, copies = 1 }) {
    // Step 1: Automatic Resolution
    const resolveRes = await this.resolvePrintConfig({
      centreId: sample.centreId || sample.centreID,
      departmentId: sample.departmentId || sample.subCategoryID,
      departmentName: sample.departmentName,
      sampleTypeId: sample.sampleTypeId,
      sampleTypeName: sample.sampleTypeName,
      containerTypeId: sample.containerTypeId,
      containerName: sample.containerName,
      testId: sample.testId,
      barcodeNo: sample.barcodeNo
    });

    if (!resolveRes.success || !resolveRes.data?.isConfigured) {
      return {
        success: false,
        error: resolveRes.data?.message || 'Barcode printing is not configured for this specimen.'
      };
    }

    const { template, primaryPrinter, fallbackPrinter, fallbackPolicy } = resolveRes.data;

    // Build normalized sample dictionary for payload tokens
    const sampleData = {
      patientName: sample.patientName || 'PATIENT',
      uhid: sample.uhid || sample.patientId || '',
      patientId: sample.patientId || '',
      labNo: sample.ledgerTransactionNo || sample.labNo || '',
      ledgerTransactionNo: sample.ledgerTransactionNo || sample.labNo || '',
      barcodeNo: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
      barcode: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
      itemName: sample.itemName || sample.testName || 'Investigation',
      testName: sample.itemName || sample.testName || 'Investigation',
      sampleTypeName: sample.sampleTypeName || 'Specimen',
      containerName: sample.containerName || 'Standard',
      departmentName: sample.departmentName || 'Pathology',
      centreName: sample.centreName || 'AURO DIAGNOSTICS',
      ageGender: sample.ageGender || `${sample.age || ''} ${sample.gender || ''}`.trim()
    };

    // Step 2: Determine Target Printer (Primary vs Fallback)
    let selectedPrinter = primaryPrinter;
    let agentResult = { success: false, status: 'UNKNOWN' };

    if (selectedPrinter) {
      // Step 3: Generate Thermal Command
      const payloadRes = await this.generateThermalPayload({
        templateId: template.templateId,
        printerId: selectedPrinter.printerId,
        protocol: selectedPrinter.protocol || 'ZPL',
        sampleData,
        copies
      });

      const rawPayload = payloadRes?.data?.rawPayload || '';

      // Step 4: Dispatch to Local Thermal Print Agent
      agentResult = await thermalAgentService.sendRawThermalJob({
        printerName: selectedPrinter.windowsPrinterName || selectedPrinter.printerName,
        protocol: selectedPrinter.protocol || 'ZPL',
        rawPayload,
        copies,
        jobTitle: `Tube Label - ${sampleData.barcodeNo}`
      });

      // Check if primary failed and automatic fallback is permitted
      if (!agentResult.success && fallbackPrinter && fallbackPolicy === 'AUTOMATIC') {
        selectedPrinter = fallbackPrinter;
        const fbPayloadRes = await this.generateThermalPayload({
          templateId: template.templateId,
          printerId: fallbackPrinter.printerId,
          protocol: fallbackPrinter.protocol || 'ZPL',
          sampleData,
          copies
        });
        agentResult = await thermalAgentService.sendRawThermalJob({
          printerName: fallbackPrinter.windowsPrinterName || fallbackPrinter.printerName,
          protocol: fallbackPrinter.protocol || 'ZPL',
          rawPayload: fbPayloadRes?.data?.rawPayload || '',
          copies,
          jobTitle: `Tube Label Fallback - ${sampleData.barcodeNo}`
        });
      }

      // Step 5: Audit the print job
      await this.logPrintJob({
        testId: sample.testId || null,
        ledgerTransactionNo: sampleData.ledgerTransactionNo,
        barcodeNo: sampleData.barcodeNo,
        patientId: sampleData.patientId,
        patientName: sampleData.patientName,
        templateId: template.templateId,
        templateVersion: template.version || 1,
        printerId: selectedPrinter.printerId,
        protocol: selectedPrinter.protocol || 'ZPL',
        status: agentResult.success ? 'PRINTED' : 'FAILED',
        attemptCount: 1,
        printedPayload: rawPayload,
        isReprint,
        reprintReason,
        errorCode: agentResult.success ? null : agentResult.status,
        errorMessage: agentResult.success ? null : agentResult.error
      });
    }

    return {
      success: agentResult.success,
      status: agentResult.status,
      printer: selectedPrinter,
      template,
      sampleData,
      isAgentOnline: agentResult.status !== 'AGENT_OFFLINE',
      error: agentResult.error,
      requiresFallbackVectorPrint: !agentResult.success
    };
  }
};

export default barcodePrintService;
